import type { EvidenceTopic } from "./evidence";

/**
 * THE SECTOR CLUSTER — the intent tier the geo pages cannot reach.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY SECTORS AND NOT MORE PLACES
 *
 * A geo page answers "who can fly a drone in Cochrane?". A consultancy does not
 * search that way. An environmental consultant working a reclamation file
 * searches "aerial imagery for reclamation monitoring Alberta". A utility asset
 * manager searches "drone powerline inspection contractor". A loss adjuster
 * searches "aerial hail damage assessment Calgary". None of those queries
 * mention a town, and none of them land on a location page.
 *
 * That is a whole tier of high-value, low-competition intent that the geo
 * architecture is structurally incapable of capturing — because it is filed
 * under the buyer's industry, not under geography.
 *
 * Each sector below is real for Alberta specifically. Energy and utilities
 * because the province runs on linear infrastructure; insurance because Calgary
 * sits in one of North America's most active hail corridors; agriculture
 * because the survey grid east of the city is the most legible farmland in the
 * country from directly overhead.
 *
 * `operationalReality` is the sector's equivalent of the airspace signal on a
 * location page: the specific, unglamorous constraint that proves the operator
 * has actually done this work rather than read about it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface Sector {
  slug: string;
  /** Short name for nav and breadcrumbs. */
  name: string;
  /** The H1. Argues, does not label. */
  h1: string;
  /** Meta description source — clamped at build. */
  description: string;
  /** The lede. One paragraph, sets the specific problem. */
  lede: string;
  /** 40–60 word quotable direct answer for the AEO block. */
  quickAnswer: string;
  /** What actually gets captured. Concrete, named. */
  captured: { subject: string; detail: string }[];
  /**
   * The sector's constraint that proves competence. The equivalent of the
   * airspace signal on a location page.
   */
  operationalReality: string[];
  /** Who inside a client organisation actually commissions this. */
  whoBuys: string[];
  /** Evidence topics this sector draws from. */
  evidenceTopics: EvidenceTopic[];
  faqs: { question: string; answer: string }[];
  /** Related sector slugs — the sideways mesh. */
  related: string[];
}

export const SECTORS: Sector[] = [
  {
    slug: "energy-and-utilities",
    name: "Energy and utilities",
    h1: "Linear infrastructure is the hardest thing to inspect and the easiest thing to fly",
    description:
      "Aerial capture for pipelines, powerlines, substations, wellsites and lease access across Alberta and BC. Advanced RPAS certified, flight-logged, repeatable from fixed coordinates.",
    lede: "A pipeline right-of-way is a hundred kilometres long and two metres wide. A ground crew inspects it at walking pace. That asymmetry is the entire argument for aerial capture in this sector.",
    quickAnswer:
      "Yes. Fly4MEdia captures pipeline right-of-way, powerline corridors, substations, wellsites and lease access across Alberta and British Columbia. Flights are conducted under an Advanced RPAS certificate with airspace authorisation filed where required, and capture points are recorded so a corridor can be re-flown identically.",
    captured: [
      {
        subject: "Right-of-way condition",
        detail:
          "Vegetation encroachment, erosion, ground disturbance and third-party activity along the corridor, captured as a continuous pass rather than sampled at access points.",
      },
      {
        subject: "Lease and wellsite condition",
        detail:
          "Site extent, containment, access road condition and surface disturbance in one plan view, dated and repeatable.",
      },
      {
        subject: "Powerline corridor",
        detail:
          "Structure condition, span clearance and vegetation proximity along the line, without a climb or a helicopter sortie.",
      },
      {
        subject: "Post-event assessment",
        detail:
          "Rapid condition capture after weather, fire or ground movement, when the schedule matters more than the resolution.",
      },
    ],
    operationalReality: [
      "Linear assets cross airspace boundaries. A single corridor can run from uncontrolled Class G into a control zone, so authorisation is established per segment rather than per project.",
      "Alberta's chinook belt produces gust spreads that ground an airframe on days the forecast calls calm. Corridor work is scheduled against a wind window, not a calendar slot.",
      "Wildfire smoke between July and early September degrades contrast enough to make condition assessment unreliable. That constraint is stated at scoping rather than discovered on delivery.",
      "Lease access roads determine what is reachable in a day. Mobilisation is quoted from the access route, not the straight-line distance.",
    ],
    whoBuys: [
      "Asset integrity and reliability managers",
      "Environmental and regulatory compliance teams",
      "Engineering consultancies working an operator's file",
      "Land and right-of-way departments",
    ],
    evidenceTopics: ["utilities", "energy", "inspection", "safety"],
    faqs: [
      {
        question: "Can a drone inspect a pipeline right-of-way in Alberta?",
        answer:
          "Yes. Right-of-way capture is a standard aerial application. The operational constraint is airspace rather than capability — a long corridor can cross from uncontrolled airspace into a control zone, so authorisation is established segment by segment before the flight.",
      },
      {
        question: "How is aerial inspection data made comparable over time?",
        answer:
          "By flying the same coordinates, altitude and heading on every visit and retaining the flight log per sortie. Without fixed capture points a second survey produces different imagery of the same asset, which cannot be compared and therefore cannot show change.",
      },
      {
        question: "Does aerial capture replace a ground inspection?",
        answer:
          "It changes what a ground crew is sent to do. Aerial capture identifies where attention is needed across the whole asset; the ground visit then addresses those locations rather than walking the entire length to find them.",
      },
      {
        question: "Can you fly a wellsite inside controlled airspace?",
        answer:
          "Yes, under an Advanced RPAS certificate with a NAV CANADA authorisation. An operator holding only a Basic certificate cannot, which is why some quotes quietly exclude sites near aerodromes or under approach paths.",
      },
    ],
    related: ["construction-and-infrastructure", "environmental-and-land", "agriculture-and-agribusiness"],
  },

  {
    slug: "construction-and-infrastructure",
    name: "Construction and infrastructure",
    h1: "Progress that was not photographed at the phase boundary cannot be photographed later",
    description:
      "Aerial progress documentation, earthworks and site logistics capture for construction and infrastructure programmes across Alberta and BC. Fixed coordinates, dated flight logs, one specification per programme.",
    lede: "Nobody can go back and shoot last March. Progress capture is the one aerial product where the value is entirely in the interval, and the interval is set by the build schedule rather than by the marketing calendar.",
    quickAnswer:
      "Yes. Fly4MEdia flies construction progress documentation across Alberta and British Columbia — repeat passes from fixed coordinates so a sequence reads as one continuous record. Airspace authorisation is filed where a site sits in controlled airspace, which covers most urban Alberta.",
    captured: [
      {
        subject: "Progress sequence",
        detail:
          "The same frame, from the same coordinates and altitude, at agreed intervals. The sequence is the deliverable; a single frame is not.",
      },
      {
        subject: "Earthworks and grading",
        detail:
          "Cut, fill and drainage across the whole footprint at once — legible from directly overhead and almost invisible at ground level.",
      },
      {
        subject: "Site logistics",
        detail:
          "Laydown organisation, access routes, crane positioning and stockpile location as a measurable state rather than an impression.",
      },
      {
        subject: "Milestone evidence",
        detail:
          "Time-stamped documentation of what existed when — the record that settles a completion-percentage argument before it becomes a dispute.",
      },
    ],
    operationalReality: [
      "Most urban Alberta construction sits inside controlled airspace. Calgary sits under the CYYC control zone and the west side sits under Springbank, so authorisation is part of scheduling rather than an obstacle to it.",
      "Interval is driven by the build schedule, not the calendar. A phase boundary that passes unphotographed is gone permanently.",
      "Active sites have crane and personnel movement. Capture is planned around the work rather than requiring the site to stop.",
      "Winter capture in Alberta gives better contrast on earthworks than midsummer, because snow cover reveals grade that green cover hides.",
    ],
    whoBuys: [
      "Project managers and site superintendents",
      "Development and asset managers",
      "Quantity surveyors and cost consultants",
      "Prime consultants reporting to an owner",
    ],
    evidenceTopics: ["construction", "inspection", "industrial", "due-diligence"],
    faqs: [
      {
        question: "How often should a construction site be captured from the air?",
        answer:
          "At phase boundaries rather than on a fixed monthly cycle. The interval is set from the build schedule at scoping, because the value of the sequence comes from capturing transitions — and a transition that passes undocumented cannot be recovered.",
      },
      {
        question: "Can aerial progress imagery be used in a payment dispute?",
        answer:
          "It is commonly used as supporting evidence. What makes it defensible is provenance — a dated flight log recording where and when the aircraft flew, and a capture specification agreed before the shoot rather than reconstructed afterwards.",
      },
      {
        question: "Does the site need to stop work for an aerial capture?",
        answer:
          "No. Capture is planned around active operations, with the flight path and timing agreed against the day's work. Stopping a site to photograph it would cost more than the imagery is worth.",
      },
    ],
    related: ["energy-and-utilities", "environmental-and-land", "insurance-and-claims"],
  },

  {
    slug: "environmental-and-land",
    name: "Environmental and land",
    h1: "Disturbance is a shape, and a shape only resolves from directly overhead",
    description:
      "Aerial capture for reclamation monitoring, disturbance mapping, watercourse and land assessment across Alberta and BC. Dated, repeatable, flight-logged imagery for environmental consultants.",
    lede: "An assessor standing on a site records condition at the points a person can reach. The extent of a disturbance, the boundary of a reclaimed area, the relationship between a working surface and a watercourse — those are shapes, and a shape is not something you can photograph from inside it.",
    quickAnswer:
      "Yes. Fly4MEdia captures reclamation progress, surface disturbance, watercourse proximity and site extent for environmental and land assessment work across Alberta and British Columbia, delivered to the consultant's specification with dated flight logs supporting the imagery.",
    captured: [
      {
        subject: "Disturbance extent",
        detail:
          "The actual footprint of a working surface against the surrounding undisturbed ground — a boundary that is estimated from the ground and measured from above.",
      },
      {
        subject: "Reclamation progress",
        detail:
          "Vegetation establishment across a reclaimed area over successive seasons, flown from identical coordinates so change is visible rather than inferred.",
      },
      {
        subject: "Watercourse relationship",
        detail:
          "Setback, crossing condition and drainage between a site and adjacent water, captured in one frame that includes both.",
      },
      {
        subject: "Access and encroachment",
        detail:
          "Trails, third-party activity and unauthorised access across a parcel, visible as pattern rather than found by walking.",
      },
    ],
    operationalReality: [
      "Reclamation is a seasonal comparison. Capture timed to a different point in the growing season than the previous survey produces imagery that cannot honestly be compared.",
      "Leaf-off in late autumn and early spring reveals ground surface, boundaries and watercourse edges that summer canopy hides completely.",
      "Provincial park land, national park land and First Nation land each carry separate restrictions that no level of RPAS certification overrides. Those are checked against the parcel before scoping, not after.",
      "Remote parcels are constrained by daylight and access rather than airspace. Mobilisation is quoted from the access route.",
    ],
    whoBuys: [
      "Environmental consultants and assessors",
      "Reclamation and closure teams",
      "Regulatory compliance managers",
      "Land agents and surface land departments",
    ],
    evidenceTopics: ["inspection", "due-diligence", "agricultural", "safety"],
    faqs: [
      {
        question: "Can aerial imagery support a reclamation assessment?",
        answer:
          "It is used routinely to document extent and vegetation establishment over time. The requirement is consistency: the same coordinates, altitude and season on each visit, with flight logs retained, so successive surveys are genuinely comparable rather than merely similar.",
      },
      {
        question: "When in the year should a land parcel be captured?",
        answer:
          "It depends on what must be shown. Leaf-off in late autumn or early spring reveals ground surface, boundaries and watercourse edges. Peak growing season shows vegetation establishment. The date is chosen from what the assessment needs to demonstrate.",
      },
      {
        question: "Are there parcels you will not fly?",
        answer:
          "Yes. National park land requires a Parks Canada permit, provincial park land carries Alberta Parks restrictions, and flight over First Nation land requires the Nation's consent. Each is checked against the parcel at scoping and stated plainly if it blocks the work.",
      },
    ],
    related: ["energy-and-utilities", "agriculture-and-agribusiness", "construction-and-infrastructure"],
  },

  {
    slug: "agriculture-and-agribusiness",
    name: "Agriculture and agribusiness",
    h1: "A quarter section is a shape you cannot see from inside it",
    description:
      "Aerial capture for agricultural land, drainage, crop condition and agribusiness facilities across Alberta. Survey-grid geometry, seasonal timing, flight-logged imagery for land transactions and operations.",
    lede: "Wheatland County east of Calgary is surveyed on the Dominion Land Survey grid — a near-perfect one-mile checkerboard that is completely invisible from a vehicle and unmistakable from a hundred and twenty metres. Everything about assessing this land depends on that view.",
    quickAnswer:
      "Yes. Fly4MEdia captures agricultural parcels, drainage patterns, crop condition and agribusiness facilities across Alberta. Most farmland sits in uncontrolled Class G airspace, so no authorisation is normally required — the binding constraints are the cultivation cycle and the wind.",
    captured: [
      {
        subject: "Parcel boundaries and access",
        detail:
          "Where the quarter actually ends, how it meets the road grid, and how much of it is genuinely workable — the three questions a land buyer cannot verify from the ground.",
      },
      {
        subject: "Drainage and soil variation",
        detail:
          "Water movement and soil pattern across the field, most legible post-seeding before the canopy closes.",
      },
      {
        subject: "Crop condition",
        detail:
          "Stand consistency and problem areas across the whole field at once, rather than sampled from the headland.",
      },
      {
        subject: "Facilities and yards",
        detail:
          "Feedlot, grain and equipment yard layout and utilisation, captured as a repeatable overhead pass.",
      },
    ],
    operationalReality: [
      "The cultivation cycle dictates the look. Post-seeding shows drainage and soil variation, mid-summer shows crop health and canola colour, and post-harvest stubble shows boundaries and access most clearly.",
      "Prairie exposure means wind is the primary scheduling variable. Wheatland and the QE2 corridor routinely hold stronger, steadier surface wind than Calgary on the same afternoon.",
      "Private aerodromes operate in otherwise open farmland. Uncontrolled airspace does not mean unoccupied airspace, so local circuits are plotted before every flight.",
      "Launch is arranged on the parcel itself with the landowner's permission, which is a scheduling dependency rather than a formality.",
    ],
    whoBuys: [
      "Farmland brokerages and land agents",
      "Agronomists and crop consultants",
      "Agribusiness operations managers",
      "Lenders and appraisers assessing agricultural security",
    ],
    evidenceTopics: ["agricultural", "inspection", "due-diligence", "real-estate"],
    faqs: [
      {
        question: "What is the best time in the growing season to photograph farmland?",
        answer:
          "It depends on the argument. Post-seeding shows drainage and soil variation, mid-summer shows crop condition and colour, and post-harvest stubble shows boundaries and access most clearly. The date is chosen from what the parcel needs to prove.",
      },
      {
        question: "Do you need authorisation to fly over farmland in Alberta?",
        answer:
          "Usually not — most agricultural land sits in uncontrolled Class G airspace. Private airfields operate throughout that airspace, though, so local circuit activity is plotted before the flight. Uncontrolled does not mean unoccupied.",
      },
      {
        question: "How does aerial imagery help sell agricultural land?",
        answer:
          "It answers what a buyer cannot verify on a site visit: exact boundaries, road and water access, drainage patterns and how much of the parcel is genuinely workable. On the survey grid east of Calgary all four are obvious from directly overhead.",
      },
    ],
    related: ["environmental-and-land", "energy-and-utilities", "construction-and-infrastructure"],
  },

  {
    slug: "insurance-and-claims",
    name: "Insurance and claims",
    h1: "The roof is where the claim is, and the roof is where nobody wants to send a person",
    description:
      "Aerial capture for property claims, hail and storm damage assessment across Alberta and BC. Dated flight logs, roof-level imagery without a ladder, captured to the adjuster's specification.",
    lede: "Calgary sits in one of North America's most active hail corridors. After a significant event the constraint on claims handling is not adjudication capacity — it is how many roofs a person can physically stand on in a day.",
    quickAnswer:
      "Yes. Fly4MEdia captures roof and property condition for claims and loss assessment across Alberta and British Columbia. Imagery is dated and flight-logged, captured at roof level without a ladder or a person at height, and delivered to the adjuster's specification.",
    captured: [
      {
        subject: "Roof condition",
        detail:
          "Surface condition at close range across the whole roof plane, including areas a ladder cannot safely reach.",
      },
      {
        subject: "Storm and hail damage",
        detail:
          "Impact pattern and distribution across a property, and across a neighbourhood where the claim requires event context.",
      },
      {
        subject: "Property extent and outbuildings",
        detail:
          "The full insured footprint in one frame — including structures a street-level inspection does not reach.",
      },
      {
        subject: "Pre-loss and post-loss comparison",
        detail:
          "Where prior capture exists, the same frame before and after the event.",
      },
    ],
    operationalReality: [
      "Post-event demand is concentrated. A hail corridor generates hundreds of claims in the same week, so surge capacity and scheduling discipline matter more than any single capture.",
      "Most Calgary addresses sit inside the CYYC control zone. An operator without an Advanced RPAS certificate cannot legally fly them, which becomes acute precisely when volume spikes.",
      "Overcast light is better than direct sun for surface damage assessment. Bright midday conditions hide impact marks that flat light reveals.",
      "Flight logs are retained per sortie, so the date and location of capture are documented rather than asserted.",
    ],
    whoBuys: [
      "Independent and staff loss adjusters",
      "Claims managers and TPAs",
      "Restoration contractors documenting scope",
      "Property managers substantiating a claim",
    ],
    evidenceTopics: ["safety", "inspection", "insurance", "real-estate"],
    faqs: [
      {
        question: "Can a drone assess hail damage on a roof?",
        answer:
          "It captures roof-level imagery of the whole plane without a ladder or a person at height, including areas that cannot be safely reached. The assessment remains the adjuster's; the aerial capture is what puts every part of the roof in front of them.",
      },
      {
        question: "Why does aerial capture matter after a hail event specifically?",
        answer:
          "Because volume is the constraint. A single storm can generate hundreds of claims in one corridor, and the limit on throughput is how many roofs a person can physically climb. Aerial capture removes the climb from the bottleneck.",
      },
      {
        question: "What light conditions are best for damage assessment?",
        answer:
          "Overcast. Flat light reveals surface impact marks that bright midday sun flattens out entirely. Scheduling a damage capture for the sunniest part of the day is a common and expensive mistake.",
      },
    ],
    related: ["construction-and-infrastructure", "environmental-and-land", "energy-and-utilities"],
  },
];

export function getSector(slug: string): Sector | undefined {
  return SECTORS.find((s) => s.slug === slug);
}
